/**
 * User State Service
 *
 * Minimal signal-based state management service for FGA users.
 * Currently provides selection state and reset functionality.
 *
 * **Note**: This service is intentionally minimal. User data for groups
 * (owners/members) is managed locally in FgaGroupViewComponent because
 * user data is group-scoped and naturally tied to component lifecycle.
 *
 * @see {@link ../components/fga-group-view/fga-group-view.component.ts} for group-scoped user state
 * @packageDocumentation
 */

import { Injectable, signal } from '@angular/core';

/**
 * Service responsible for managing user selection state using Angular signals.
 *
 * This service follows the element-scoped pattern (no `providedIn: 'root'`)
 * to ensure state isolation per `<fga-manager>` instance.
 *
 * @example
 * ```typescript
 * const userState = inject(UserStateService);
 *
 * // Select a user
 * userState.selectUser('user:waad|org_demo|abc123');
 *
 * // Check selection
 * const selectedId = userState.selectedUserId();
 *
 * // Reset on cleanup
 * userState.reset();
 * ```
 */
@Injectable()
export class UserStateService {
  // ---------------------------------------------------------------------------
  // Private Writable Signals
  // ---------------------------------------------------------------------------

  /**
   * Internal writable signal for the selected user ID
   */
  private readonly _selectedUserId = signal<string | null>(null);

  // ---------------------------------------------------------------------------
  // Public Readonly Signals
  // ---------------------------------------------------------------------------

  /**
   * Readonly signal exposing the currently selected user ID.
   */
  readonly selectedUserId = this._selectedUserId.asReadonly();

  // ---------------------------------------------------------------------------
  // Mutation Methods
  // ---------------------------------------------------------------------------

  /**
   * Sets the selected user ID.
   *
   * @param userId - ID of the user to select, or null to clear selection
   */
  selectUser(userId: string | null): void {
    this._selectedUserId.set(userId);
  }

  /**
   * Resets all state to initial values.
   */
  reset(): void {
    this._selectedUserId.set(null);
  }
}
